import { messageError, messageSuccess, PageSection } from '@deltaohm/ant-components';
import {
  Alert,
  Col,
  Row,
  Typography,
  Result,
  Button,
} from 'antd';

import { useTranslation } from 'react-i18next';
import ErrorList from 'antd/lib/form/ErrorList';
import React from 'react';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import SerialPort from '../utils/serial';
import { XPT800_CLAIM_OWNER_MUTATION } from './queries';

const fetchInformation = async (serialPort) => {
  const serialNumber = await serialPort.writeStringWithResponseString('CMD:INSTR_ID:?\r\n', 3000, (data) => {
    if (data.startsWith('CMD:INSTR_ID:?:')) {
      const stripped = data.replace('CMD:INSTR_ID:?:', '');
      const splitted = stripped.split(' ');
      return splitted[1].trim();
    }
    return null;
  });

  const preAmpliSerialNumber = await serialPort.writeStringWithResponseString('CMD:PRE_ID:?\r\n', 3000, (data) => {
    if (data.startsWith('CMD:PRE_ID:?:')) {
      const stripped = data.replace('CMD:PRE_ID:?:', '');
      const splitted = stripped.split(' ');
      return splitted[1].trim();
    }
    return null;
  });

  const micSerialNumber = await serialPort.writeStringWithResponseString('CMD:MIC_ID:?\r\n', 3000, (data) => {
    if (data.startsWith('CMD:MIC_ID:?:')) {
      const stripped = data.replace('CMD:MIC_ID:?:', '');
      const splitted = stripped.split(' ');
      return splitted[1].trim();
    }
    return null;
  });

  if (!serialNumber || !preAmpliSerialNumber || !micSerialNumber) {
    return null;
  }

  return {
    serialNumber,
    preAmpliSerialNumber,
    micSerialNumber,
  };
};

const SoundMeterXPT800ManagePage = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [claimOwnerAction] = useMutation(XPT800_CLAIM_OWNER_MUTATION);

  const handleConnectError = () => {
    messageError({
      content: (
        <>
          <Typography.Title level={5}>
            {t('localSoundMeters.connect.cannotConnect.title')}
          </Typography.Title>
          <ErrorList errors={[
            t('localSoundMeters.connect.cannotConnect.link'),
            t('localSoundMeters.connect.cannotConnect.port'),
            t('localSoundMeters.connect.cannotConnect.model'),
            t('localSoundMeters.connect.cannotConnect.usb'),
          ]}
          />
        </>
      ),
    });
  };

  const handleConnect = async () => {
    const port = await SerialPort.connect({
      baudRate: 230400,
      // flowControl: 'hardware',
      bufferSize: 1024,
    });

    const serialPort = new SerialPort(port);

    if (!serialPort) {
      throw new Error();
    }

    const result = await fetchInformation(serialPort);
    if (result) {
      try {
        await claimOwnerAction({
          variables: {
            input: {
              serialNumber: result.serialNumber,
            },
          },
        });
        await serialPort.disconnect();
        messageSuccess(t('soundMeterXPT800.connect.claimSuccess'));
        navigate('/devices', { replace: true });
      }
      catch (e) {
        await serialPort.disconnect();
        messageError({
          content: (
            <>
              <Typography.Title level={5}>
                {t('soundMeterXPT800.connect.alreadyBound.title')}
              </Typography.Title>
              <Typography.Text>
                {t('soundMeterXPT800.connect.alreadyBound.description')}
              </Typography.Text>
            </>
          ),
        });
      }
    }
    else {
      await serialPort.disconnect();
      handleConnectError();
    }
  };

  const renderConnect = () => {
    if (!SerialPort.isAvailable()) {
      return (
        <Row>
          <Col xs={24}>
            <Alert
              message={t('localSoundMeters.connect.serial')}
              description={t('localSoundMeters.connect.serialUnavailable')}
              type="error"
              showIcon
            />
          </Col>
        </Row>
      );
    }
    return (
      <Row>
        <Col xs={24}>
          <Result
            title={t('soundMeterXPT800.connect.infoTitle')}
            subTitle={t('soundMeterXPT800.connect.infoSubTitle')}
            extra={[
              <Button type="primary" key="connect" onClick={handleConnect}>
                {t('soundMeterXPT800.connect.connectButton')}
              </Button>,
            ]}
          />
        </Col>
      </Row>
    );
  };

  return (
    <PageSection
      title={t('soundMeterXPT800.manage.title')}
      subtitle={t('soundMeterXPT800.manage.subtitle')}
    >
      {renderConnect()}
    </PageSection>
  );
};

export default SoundMeterXPT800ManagePage;
